import React, { useEffect, useMemo, useState } from 'react';
import './style.less';
import { SpotlightToggleCard } from 'browser/components/Spotlight/Account/ToggleCard';
import { Icon } from 'shared/components/Icon';
import { SpotlightAccount, SpotlightIntent, SpotlightTrend } from 'types/spotlight';
import { getSpotlightAccount, getSpotlightAccountVisitors } from 'shared/api';
import { formatDateShort, formatRevenue } from 'shared/utils/helpers';
import { Button } from 'shared/components/Button';
import { SpotlightSocials } from 'browser/components/Spotlight/Account/Socials';
import { UniqueVisitor } from 'sb-lib/types';
import clsx from 'clsx';
import { Tooltip } from 'shared/components/Tooltip';
import ReactTimeAgo from 'react-time-ago';
import TimeAgo from 'javascript-time-ago';
import en from 'javascript-time-ago/locale/en.json';
import Skeleton from 'react-loading-skeleton';

TimeAgo.addDefaultLocale(en);
TimeAgo.addLocale(en);

export const VisitorOverviewAccount: React.FC<{ accountId?: number }> = ({
  accountId,
}) => {
  const [account, setAccount] = useState<SpotlightAccount | undefined>();
  const [visitors, setVisitors] = useState<UniqueVisitor[] | undefined>([]);

  const [loading, setLoading] = useState(true);

  const [hasError, setHasError] = useState<boolean>(!account?.domain);

  const fullUrl = account?.domain?.startsWith('https://')
    ? account?.domain
    : `https://${account?.domain}`;

  const location = useMemo(() => {
    if (!account) return;

    if (account?.clearbitData?.location) {
      return account?.clearbitData?.location;
    }

    return account?.location;
  }, [account]);

  const employeeCount = useMemo(() => {
    if (!account) return;

    if (account?.clearbitData?.metrics?.employees) {
      return account?.clearbitData?.metrics.employees;
    }

    if (account?.clearbitData?.metrics?.employeesRange) {
      return account?.clearbitData?.metrics?.employeesRange;
    }

    if (!account.sizeEstimateHigh && !account.sizeEstimateLow) {
      return;
    }

    if (account.sizeEstimateHigh === account.sizeEstimateLow) {
      return account.sizeEstimateHigh;
    }

    return `${account.sizeEstimateLow}-${account.sizeEstimateHigh}`;
  }, [account]);

  const revenue = useMemo(() => {
    if (!account) return;

    if (account?.clearbitData?.metrics?.estimatedAnnualRevenue) {
      return account?.clearbitData?.metrics.estimatedAnnualRevenue;
    }

    return account?.revenue ? formatRevenue(account.revenue) : undefined;
  }, [account]);

  const industry = useMemo(() => {
    if (!account) return;

    if (account?.clearbitData?.category?.subIndustry) {
      return account?.clearbitData?.category.subIndustry;
    }

    if (account?.clearbitData?.category?.industry) {
      return account?.clearbitData?.category.industry;
    }

    return account?.industry?.toLocaleLowerCase()?.replace(/_/g, ' ');
  }, [account]);

  const lastVisit = useMemo(() => {
    if (!account?.lastVisitDate) return <>N/A</>;
    return (
      <Tooltip
        title={formatDateShort((account.lastVisitDate as unknown) as number)}
        theme={'dark'}
      >
        <ReactTimeAgo date={new Date(`${account?.lastVisitDate}Z`)} locale="en-US" />
      </Tooltip>
    );
  }, [account]);

  useEffect(() => {
    setHasError(!account?.domain);
  }, [account]);

  useEffect(() => {
    setLoading(true);

    if (!accountId) {
      return;
    }

    const promises = [
      getSpotlightAccount((accountId as unknown) as number).then(({ data }) => {
        if (!data) {
          return;
        }
        setAccount(data);
      }),
      getSpotlightAccountVisitors((accountId as unknown) as number).then(({ data }) => {
        setVisitors(data);
      }),
    ];

    Promise.all(promises).then(() => setLoading(false));
  }, [accountId]);

  const trend = useMemo(() => {
    switch (account?.trend) {
      case SpotlightTrend.SURGING:
        return `🔥 Surging`;
      case SpotlightTrend.INCREASING:
        return `🚀 Increasing`;
      case SpotlightTrend.NEUTRAL:
        return `😐 Neutral`;
      case SpotlightTrend.DECREASING:
        return `📉 Decreasing`;
      default:
        return ``;
    }
  }, [account?.trend]);

  if (loading) {
    return (
      <SpotlightToggleCard
        title={
          <>
            <Icon icon="building2" />
            Account
          </>
        }
      >
        <div className="VisitorOverviewAccount__top">
          <div className="VisitorOverviewAccount__content">
            <div className="VisitorOverviewAccount__logo">
              <Skeleton
                style={{ borderRadius: '6px' }}
                width={72}
                height={72}
                baseColor="#F6F7F9"
              />
            </div>
            <div className="VisitorOverviewAccount__name">
              <Skeleton
                style={{ borderRadius: '6px' }}
                width={72}
                height={16}
                baseColor="#F6F7F9"
              />
              <Skeleton
                style={{ borderRadius: '6px' }}
                width={82}
                height={10}
                baseColor="#F6F7F9"
              />
            </div>
          </div>
        </div>
        <div className="VisitorOverviewAccount__details">
          <div className="VisitorOverviewAccount__list">
            {location && (
              <div className="VisitorOverviewAccount__item">
                <Icon icon="pinOutline" />
                <span>
                  <strong>Location:</strong>
                  <Skeleton
                    style={{ borderRadius: '6px' }}
                    width={100}
                    height={10}
                    baseColor="#F6F7F9"
                  />
                </span>
              </div>
            )}
            {employeeCount && (
              <div className="VisitorOverviewAccount__item">
                <Icon icon="hash" />
                <span>
                  <strong>Employee Count:</strong>
                  <Skeleton
                    style={{ borderRadius: '6px' }}
                    width={100}
                    height={10}
                    baseColor="#F6F7F9"
                  />
                </span>
              </div>
            )}
            {revenue && (
              <div className="VisitorOverviewAccount__item">
                <Icon icon="dollar" />
                <span>
                  <strong>Revenue:</strong>
                  <Skeleton
                    style={{ borderRadius: '6px' }}
                    width={100}
                    height={10}
                    baseColor="#F6F7F9"
                  />
                </span>
              </div>
            )}
            {industry && (
              <div className="VisitorOverviewAccount__item">
                <Icon icon="industry" />
                <span>
                  <strong>Industry:</strong>
                  <Skeleton
                    style={{ borderRadius: '6px' }}
                    width={100}
                    height={10}
                    baseColor="#F6F7F9"
                  />
                </span>
              </div>
            )}
          </div>

          <div className="VisitorOverviewAccount__table">
            <div className="VisitorOverviewAccount__tableItem">
              <span>Visitors</span>
              <Skeleton
                style={{ borderRadius: '6px' }}
                width={30}
                height={10}
                baseColor="#F6F7F9"
              />
            </div>
            <div className="VisitorOverviewAccount__tableItem">
              <span>Intent</span>
              <Skeleton
                style={{ borderRadius: '6px' }}
                width={30}
                height={10}
                baseColor="#F6F7F9"
              />
            </div>
            <div className="VisitorOverviewAccount__tableItem">
              <span>Trend</span>
              <Skeleton
                style={{ borderRadius: '6px' }}
                width={30}
                height={10}
                baseColor="#F6F7F9"
              />
            </div>
            <div className="VisitorOverviewAccount__tableItem">
              <span>Last Seen</span>
              <Skeleton
                style={{ borderRadius: '6px' }}
                width={30}
                height={10}
                baseColor="#F6F7F9"
              />
            </div>
          </div>
        </div>
        <Button
          className="VisitorOverviewAccount__button"
          disabled
          type="ghost"
          target="_blank"
          rel="noreferrer"
        >
          See Account
          <Icon icon="arrowLongLeft" width={16} height={16} />
        </Button>
      </SpotlightToggleCard>
    );
  }
  return (
    <SpotlightToggleCard
      title={
        <>
          <Icon icon="building2" />
          Account
        </>
      }
    >
      <div className="VisitorOverviewAccount__top">
        <div className="VisitorOverviewAccount__content">
          <div className="VisitorOverviewAccount__logo">
            {!loading && (
              <>
                {!hasError ? (
                  <img
                    src={`https://logo.clearbit.com/${account?.domain}`}
                    loading="lazy"
                    onError={() => setHasError(true)}
                  />
                ) : (
                  <Icon icon="company2" width={24} height={24} />
                )}
              </>
            )}
          </div>
          <div className="VisitorOverviewAccount__name">
            {account?.name}
            {account?.domain && (
              <a
                href={fullUrl}
                onClick={e => {
                  e.stopPropagation();
                }}
                target="_blank"
                rel="noopener noreferrer"
              >
                {account?.domain}
                <Icon icon="linkExternal" width={16} height={16} />
              </a>
            )}
          </div>
          <SpotlightSocials account={account} />
        </div>
      </div>
      <div className="VisitorOverviewAccount__details">
        <div className="VisitorOverviewAccount__list">
          {location && (
            <div className="VisitorOverviewAccount__item">
              <Icon icon="pinOutline" />
              <span>
                <strong>Location:</strong>
                {location}
              </span>
            </div>
          )}
          {employeeCount && (
            <div className="VisitorOverviewAccount__item">
              <Icon icon="hash" />
              <span>
                <strong>Employee Count:</strong>
                {employeeCount}
              </span>
            </div>
          )}
          {revenue && (
            <div className="VisitorOverviewAccount__item">
              <Icon icon="dollar" />
              <span>
                <strong>Revenue:</strong>
                {revenue}
              </span>
            </div>
          )}
          {industry && (
            <div className="VisitorOverviewAccount__item">
              <Icon icon="industry" />
              <span>
                <strong>Industry:</strong>
                {industry}
              </span>
            </div>
          )}
        </div>

        <div className="VisitorOverviewAccount__table">
          <div className="VisitorOverviewAccount__tableItem">
            <span>Visitors</span>
            {visitors?.length}
          </div>
          <div className="VisitorOverviewAccount__tableItem">
            <span>Intent</span>
            <div className="VisitorOverviewAccount__intentWrap">
              <div
                className={clsx(
                  'VisitorOverviewAccount__intent',
                  `VisitorOverviewAccount__intent--${account?.intent}`,
                )}
              >
                {Object.keys(SpotlightIntent).map(k => {
                  return (
                    <div className="VisitorOverviewAccount__intent-bar" key={k}></div>
                  );
                })}
              </div>
              {account?.intent.replaceAll('_', ' ')}
            </div>
          </div>
          <div className="VisitorOverviewAccount__tableItem">
            <span>Trend</span>
            <div className="VisitorOverviewAccount__trendWrap">
              <div
                className={clsx(
                  'VisitorOverviewAccount__trend',
                  `VisitorOverviewAccount__trend--${account?.trend}`,
                )}
              >
                {trend}
              </div>
            </div>
          </div>
          <div className="VisitorOverviewAccount__tableItem">
            <span>Last Seen</span>
            {lastVisit}
          </div>
        </div>
      </div>
      <Button
        className="VisitorOverviewAccount__button"
        href={`/spotlight/account/${accountId}`}
        type="ghost"
        target="_blank"
        rel="noreferrer"
      >
        See Account
        <Icon icon="arrowLongLeft" width={16} height={16} />
      </Button>
    </SpotlightToggleCard>
  );
};
